import { createPageShell } from '../ui/pageShell.js';
import { el, btn } from '../ui/dom.js';
import { getCurrentUser } from '../auth/session.js';
import { fetchBoardSettings, updateBoardSettings } from './api.js';
import {
  BOARD_CELL_TYPES,
  normalizeBoardSettings,
  sumBoardWeights,
  type BoardCellType,
  type BoardSettings,
} from '@multirx/shared/types/board.js';

const CELL_LABELS: Record<BoardCellType, string> = {
  upgradeMeeting: 'Amélioration de meeting',
  malusMeeting: 'Malus de meeting',
  upgradeCarPermanent: 'Amélioration voiture (permanente)',
  upgradeDriverPermanent: 'Amélioration pilote (permanente)',
  jokerSeasonUpgrade: 'Bonus Joker saisonnier',
  jokerMeetingUpgrade: 'Bonus Joker de meeting',
  randomCard: 'Carte aléatoire',
  carPurchase: 'Achat de voiture',
  casino: 'Casino',
  bonusPoints: 'Bonus de points',
  malusPoints: 'Malus de points',
};

function numberField(
  label: string,
  value: number,
  opts: { min?: number; step?: number } = {}
): { row: HTMLElement; input: HTMLInputElement } {
  const row = el('label', { class: 'label' });
  row.appendChild(el('span', { text: label }));
  const input = document.createElement('input');
  input.type = 'number';
  input.className = 'input';
  input.value = String(value);
  input.min = String(opts.min ?? 0);
  input.step = String(opts.step ?? 1);
  row.appendChild(input);
  return { row, input };
}

export function mountBoardSettingsApp(root: HTMLElement): void {
  root.innerHTML = '';
  root.className = 'app-view circuits-app';

  const { page, body } = createPageShell({
    title: 'Réglages du plateau',
    subtitle: 'Poids des cases et coûts du tour de chauffe avant chaque manche de qualif.',
    breadcrumbs: [{ label: 'Tableau de bord', href: '#dashboard' }, { label: 'Plateau' }],
  });

  const section = el('section', { class: 'card settings-card' });

  if (getCurrentUser()?.role !== 'admin') {
    section.innerHTML = '<p>Accès réservé aux administrateurs.</p>';
    body.appendChild(section);
    root.appendChild(page);
    return;
  }

  section.innerHTML = '<p>Chargement…</p>';
  body.appendChild(section);
  root.appendChild(page);

  void fetchBoardSettings()
    .then((settings) => renderForm(section, settings))
    .catch((err: Error) => {
      section.innerHTML = `<p class="auth-error">${err.message}</p>`;
    });
}

function renderForm(section: HTMLElement, settings: BoardSettings): void {
  section.innerHTML = '';

  const weightInputs = new Map<BoardCellType, HTMLInputElement>();
  const sumLabel = el('p', { class: 'page-subtitle' });

  const updateSum = (): void => {
    const raw: Record<string, number> = {};
    for (const [type, input] of weightInputs) raw[type] = Number(input.value) || 0;
    const sum = sumBoardWeights(raw);
    sumLabel.textContent = `Somme des poids : ${sum} / 100`;
    sumLabel.classList.toggle('auth-error', sum !== 100);
  };

  section.appendChild(el('h2', { class: 'hub-card-title', text: 'Poids des cases (%)' }));
  const weightsGrid = el('div', { class: 'settings-theme-option' });
  weightsGrid.style.display = 'grid';
  weightsGrid.style.gridTemplateColumns = 'repeat(auto-fill, minmax(220px, 1fr))';
  weightsGrid.style.gap = '0.75rem';
  for (const type of BOARD_CELL_TYPES) {
    const { row, input } = numberField(CELL_LABELS[type], settings.weights[type], { min: 0 });
    input.addEventListener('input', updateSum);
    weightInputs.set(type, input);
    weightsGrid.appendChild(row);
  }
  section.appendChild(weightsGrid);
  section.appendChild(sumLabel);
  updateSum();

  section.appendChild(el('h2', { class: 'hub-card-title', text: 'Coûts (points de course)' }));
  const costsGrid = el('div', { class: 'settings-theme-option' });
  costsGrid.style.display = 'grid';
  costsGrid.style.gridTemplateColumns = 'repeat(auto-fill, minmax(220px, 1fr))';
  costsGrid.style.gap = '0.75rem';
  const costUpgradeMeeting = numberField('Amélioration de meeting', settings.costUpgradeMeeting);
  const costMalusAvoid = numberField('Éviter un malus de meeting', settings.costMalusAvoid);
  const costCarPermanent = numberField(
    'Amélioration voiture permanente',
    settings.costCarPermanent
  );
  const costDriverPermanent = numberField(
    'Amélioration pilote permanente',
    settings.costDriverPermanent
  );
  const costJokerSeason = numberField('Bonus Joker saisonnier', settings.costJokerSeason);
  const costJokerMeeting = numberField('Bonus Joker de meeting', settings.costJokerMeeting);
  const costCarPurchase = numberField('Achat de voiture', settings.costCarPurchase);
  [
    costUpgradeMeeting,
    costMalusAvoid,
    costCarPermanent,
    costDriverPermanent,
    costJokerSeason,
    costJokerMeeting,
    costCarPurchase,
  ].forEach(({ row }) => costsGrid.appendChild(row));
  section.appendChild(costsGrid);

  section.appendChild(el('h2', { class: 'hub-card-title', text: 'Plages de points' }));
  const rangesGrid = el('div', { class: 'settings-theme-option' });
  rangesGrid.style.display = 'grid';
  rangesGrid.style.gridTemplateColumns = 'repeat(auto-fill, minmax(220px, 1fr))';
  rangesGrid.style.gap = '0.75rem';
  const bonusMin = numberField('Bonus de points — minimum', settings.bonusPointsMin);
  const bonusMax = numberField('Bonus de points — maximum', settings.bonusPointsMax);
  const malusMin = numberField('Malus de points — minimum', settings.malusPointsMin);
  const malusMax = numberField('Malus de points — maximum', settings.malusPointsMax);
  const casinoMultiplier = numberField('Multiplicateur casino', settings.casinoMultiplier, {
    min: 1,
  });
  [bonusMin, bonusMax, malusMin, malusMax, casinoMultiplier].forEach(({ row }) =>
    rangesGrid.appendChild(row)
  );
  section.appendChild(rangesGrid);

  const errorLine = el('p', { class: 'auth-error' });
  errorLine.style.display = 'none';
  section.appendChild(errorLine);

  const actions = el('div', { class: 'page-actions' });
  actions.style.marginTop = '1rem';
  const saveBtn = btn('Enregistrer', { variant: 'primary' }, () => {
    errorLine.style.display = 'none';
    const raw = {
      weights: Object.fromEntries(
        Array.from(weightInputs, ([type, input]) => [type, Number(input.value) || 0])
      ),
      costUpgradeMeeting: Number(costUpgradeMeeting.input.value),
      costMalusAvoid: Number(costMalusAvoid.input.value),
      costCarPermanent: Number(costCarPermanent.input.value),
      costDriverPermanent: Number(costDriverPermanent.input.value),
      costJokerSeason: Number(costJokerSeason.input.value),
      costJokerMeeting: Number(costJokerMeeting.input.value),
      costCarPurchase: Number(costCarPurchase.input.value),
      bonusPointsMin: Number(bonusMin.input.value),
      bonusPointsMax: Number(bonusMax.input.value),
      malusPointsMin: Number(malusMin.input.value),
      malusPointsMax: Number(malusMax.input.value),
      casinoMultiplier: Number(casinoMultiplier.input.value),
    };
    const sum = sumBoardWeights(raw.weights);
    if (sum !== 100) {
      errorLine.textContent = `La somme des poids doit être exactement 100 (actuellement ${sum}).`;
      errorLine.style.display = '';
      return;
    }
    const normalized = normalizeBoardSettings(raw);
    saveBtn.disabled = true;
    void updateBoardSettings(normalized)
      .then((saved) => renderForm(section, saved))
      .catch((err: Error) => {
        errorLine.textContent = err.message;
        errorLine.style.display = '';
      })
      .finally(() => {
        saveBtn.disabled = false;
      });
  });
  actions.appendChild(saveBtn);
  actions.appendChild(
    btn('Retour au tableau de bord', { variant: 'ghost' }, () => {
      window.location.hash = 'dashboard';
    })
  );
  section.appendChild(actions);
}
